import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AIAssistant } from "../components/ai/AIAssistant";
import {
  Building2,
  DollarSign,
  TrendingUp,
  Users,
  MessageSquare,
  Star,
  CheckCircle,
  Clock,
  MapPin,
  Briefcase,
  CreditCard,
  ArrowRight,
  Eye,
  Send,
  Filter,
  RefreshCw,
  AlertCircle,
  Home,
  Percent
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LenderPortal() {
  const navigate = useNavigate();
  const [lender, setLender] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [interestForm, setInterestForm] = useState({
    estimated_rate: "",
    estimated_points: "",
    proposed_terms: "",
    notes: ""
  });

  useEffect(() => {
    fetchLenderProfile();
  }, []);

  const fetchLenderProfile = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/lender/me`, { withCredentials: true });
      setLender(res.data);
      fetchDashboard();
    } catch (err) {
      navigate("/lender/login");
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/lender/dashboard`, { withCredentials: true });
      setDashboard(res.data);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpressInterest = async (requestId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/lender/express-interest`,
        {
          funding_request_id: requestId,
          ...interestForm,
          estimated_points: interestForm.estimated_points ? parseFloat(interestForm.estimated_points) : null
        },
        { withCredentials: true }
      );
      toast.success("Interest expressed successfully!");
      setShowInterestModal(false);
      setInterestForm({ estimated_rate: "", estimated_points: "", proposed_terms: "", notes: "" });
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to express interest");
    }
  };

  const handleWithdrawInterest = async (requestId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/lender/withdraw-interest/${requestId}`,
        { withCredentials: true }
      );
      toast.success("Interest withdrawn");
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to withdraw interest");
    }
  };

  const viewRequestDetails = async (request) => {
    setSelectedRequest(request);
    // Fetch messages if we've expressed interest
    if (request.has_expressed_interest) {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/lender/messages/${request.request_id}`,
          { withCredentials: true }
        );
        setMessages(res.data.messages || []);
      } catch (err) {
        console.error("Failed to fetch messages:", err);
      }
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedRequest) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/lender/messages`,
        {
          funding_request_id: selectedRequest.request_id,
          recipient_type: "investor",
          recipient_id: selectedRequest.user_id,
          content: newMessage
        },
        { withCredentials: true }
      );
      setNewMessage("");
      // Refresh messages
      const res = await axios.get(
        `${BACKEND_URL}/api/lender/messages/${selectedRequest.request_id}`,
        { withCredentials: true }
      );
      setMessages(res.data.messages || []);
    } catch (err) {
      toast.error("Failed to send message");
    }
  };

  const handleLogout = async () => {
    await axios.post(`${BACKEND_URL}/api/lender/logout`, {}, { withCredentials: true });
    navigate("/lender/login");
  };

  const handleSubscribe = async () => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/lender/subscribe`,
        {},
        { withCredentials: true }
      );
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Failed to start subscription");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-black border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gold to-gold rounded-2xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Lender Portal</h1>
              <p className="text-xs text-white/70">{lender?.company_name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/lender/profile" 
              className="text-sm text-white/70 hover:text-white transition-all duration-300"
            >
              Profile
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white/70 hover:text-white"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Subscription Required Banner */}
        {dashboard?.subscription_required && (
          <div className="mb-8 p-6 bg-gradient-to-r from-gold/10 to-gold/10 border border-gold/30 rounded-2xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Subscribe to Access Deals</h2>
                <p className="text-white/70">
                  Get access to exclusive funding requests from verified real estate investors.
                </p>
                <ul className="mt-3 space-y-1 text-sm text-white/70">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-gold" />
                    View anonymized deal requests
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-gold" />
                    Express interest and connect with investors
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-gold" />
                    Secure messaging platform
                  </li>
                </ul>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-white">$199<span className="text-lg text-white/70">/mo</span></div>
                <Button
                  onClick={handleSubscribe}
                  className="mt-3 bg-gold hover:bg-gold"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscribe Now
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-black border border-white/[0.06] p-4 rounded-2xl">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-2">
              <Briefcase className="w-4 h-4" />
              Deals Funded
            </div>
            <div className="text-2xl font-bold text-white">
              {dashboard?.stats?.deals_funded || 0}
            </div>
          </div>
          
          <div className="bg-black border border-white/[0.06] p-4 rounded-2xl">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-2">
              <DollarSign className="w-4 h-4" />
              Total Funded
            </div>
            <div className="text-2xl font-bold text-gold">
              {formatCurrency(dashboard?.stats?.total_funded_amount)}
            </div>
          </div>
          
          <div className="bg-black border border-white/[0.06] p-4 rounded-2xl">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-2">
              <Star className="w-4 h-4" />
              Rating
            </div>
            <div className="text-2xl font-bold text-gold">
              {dashboard?.stats?.rating ? `${dashboard.stats.rating.toFixed(1)}` : "N/A"}
            </div>
          </div>
          
          <div className="bg-black border border-white/[0.06] p-4 rounded-2xl">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-2">
              <TrendingUp className="w-4 h-4" />
              Active Interests
            </div>
            <div className="text-2xl font-bold text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]">
              {dashboard?.stats?.active_interests || 0}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Funding Requests Column */}
          <div className="col-span-1 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">
                Matching Funding Requests
                {dashboard?.total_matching > 0 && (
                  <span className="ml-2 text-sm text-white/70">
                    ({dashboard.total_matching} total)
                  </span>
                )}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchDashboard}
                className="border-white/[0.08] text-white/70"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {!dashboard?.subscription_required && dashboard?.funding_requests?.length === 0 && (
              <div className="bg-black border border-white/[0.06] rounded-2xl p-8 text-center">
                <AlertCircle className="w-12 h-12 text-white/70 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">No Matching Requests</h3>
                <p className="text-sm text-white/70">
                  Update your lending criteria to see more opportunities.
                </p>
                <Link to="/lender/profile">
                  <Button className="mt-4" variant="outline">
                    Update Criteria
                  </Button>
                </Link>
              </div>
            )}

            <div className="space-y-4">
              {dashboard?.funding_requests?.map((request) => (
                <div
                  key={request.request_id}
                  className={`bg-black border rounded-2xl p-4 transition-all duration-300 cursor-pointer ${
                    selectedRequest?.request_id === request.request_id
                      ? "border-gold"
                      : "border-white/[0.06] hover:border-white/[0.08]"
                  }`}
                  onClick={() => viewRequestDetails(request)}
                  data-testid="funding-request-card"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4 text-white/70" />
                        <span className="font-bold text-white">
                          {request.deal_summary?.property_type || "Property"}
                        </span>
                        <span className="text-white/70">•</span>
                        <span className="text-white/70 text-sm">
                          {request.deal_summary?.state || "Unknown"}, {request.deal_summary?.county || ""}
                        </span>
                      </div>
                      <div className="text-sm text-white/70 mt-1">
                        {request.loan_type?.replace("_", " ")} • {request.timeline_days} day timeline
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gold">
                        {formatCurrency(request.loan_amount_requested)}
                      </div>
                      <div className="text-xs text-white/70">requested</div>
                    </div>
                  </div>

                  {/* Deal Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
                    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 p-2 rounded">
                      <div className="text-white/70 text-xs">ARV</div>
                      <div className="text-white font-medium">
                        {formatCurrency(request.deal_summary?.arv)}
                      </div>
                    </div>
                    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 p-2 rounded">
                      <div className="text-white/70 text-xs">Purchase</div>
                      <div className="text-white font-medium">
                        {formatCurrency(request.deal_summary?.asking_price)}
                      </div>
                    </div>
                    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 p-2 rounded">
                      <div className="text-white/70 text-xs">LTV</div>
                      <div className="text-white font-medium">
                        {request.deal_summary?.ltv_percent || 0}%
                      </div>
                    </div>
                    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 p-2 rounded">
                      <div className="text-white/70 text-xs">ARV %</div>
                      <div className="text-white font-medium">
                        {request.deal_summary?.arv_percent || 0}%
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
                    <div className="flex items-center gap-2 text-xs text-white/70">
                      <Clock className="w-3 h-3" />
                      {new Date(request.created_at).toLocaleDateString()}
                    </div>
                    
                    {request.has_expressed_interest ? (
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-gold/20 text-gold text-xs rounded">
                          Interest Expressed
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#FF3B30] hover:text-red-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWithdrawInterest(request.request_id);
                          }}
                        >
                          Withdraw
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-gold hover:bg-gold"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRequest(request);
                          setShowInterestModal(true);
                        }}
                      >
                        Express Interest
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detail/Messages Column */}
          <div className="col-span-1">
            {selectedRequest ? (
              <div className="bg-black border border-white/[0.06] rounded-2xl sticky top-4">
                <div className="p-4 border-b border-white/[0.06]">
                  <h3 className="font-bold text-white">Request Details</h3>
                  <p className="text-sm text-white/70">
                    {selectedRequest.deal_summary?.property_type} in {selectedRequest.deal_summary?.state}
                  </p>
                </div>

                {/* Deal Info */}
                <div className="p-4 border-b border-white/[0.06] space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/70 text-sm">Loan Amount</span>
                    <span className="text-white font-medium">
                      {formatCurrency(selectedRequest.loan_amount_requested)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70 text-sm">Property Type</span>
                    <span className="text-white">
                      {selectedRequest.deal_summary?.property_type}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70 text-sm">Beds/Baths</span>
                    <span className="text-white">
                      {selectedRequest.deal_summary?.beds || 0}/{selectedRequest.deal_summary?.baths || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70 text-sm">Sqft</span>
                    <span className="text-white">
                      {selectedRequest.deal_summary?.sqft?.toLocaleString() || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70 text-sm">Projected Profit</span>
                    <span className="text-gold font-medium">
                      {formatCurrency(selectedRequest.deal_summary?.projected_profit)}
                    </span>
                  </div>
                  {selectedRequest.notes && (
                    <div className="pt-2 border-t border-white/[0.06]">
                      <span className="text-white/70 text-sm block mb-1">Notes</span>
                      <p className="text-white text-sm">{selectedRequest.notes}</p>
                    </div>
                  )}
                </div>

                {/* Messages */}
                {selectedRequest.has_expressed_interest && (
                  <div className="p-4">
                    <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Messages
                    </h4>
                    
                    <div className="h-48 overflow-y-auto space-y-2 mb-3 bg-[#0A0A0A]/80 backdrop-blur-sm/30 rounded p-2">
                      {messages.length === 0 ? (
                        <p className="text-xs text-white/70 text-center py-4">
                          No messages yet. Start the conversation!
                        </p>
                      ) : (
                        messages.map((msg) => (
                          <div
                            key={msg.message_id}
                            className={`p-2 rounded text-sm ${
                              msg.sender_type === "lender"
                                ? "bg-gold/20 text-gold-light ml-4"
                                : "bg-white/[0.08] text-white/90 mr-4"
                            }`}
                          >
                            <div className="text-xs opacity-70 mb-1">
                              {msg.sender_name || msg.sender_type}
                            </div>
                            {msg.content}
                          </div>
                        ))
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white text-sm"
                        onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                      />
                      <Button
                        size="icon"
                        onClick={sendMessage}
                        className="bg-gold hover:bg-gold"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-black border border-white/[0.06] rounded-2xl p-8 text-center">
                <Eye className="w-12 h-12 text-white/70 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">Select a Request</h3>
                <p className="text-sm text-white/70">
                  Click on a funding request to view details and message the investor.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Interest Modal */}
      {showInterestModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-black border border-white/[0.08] rounded-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-white mb-4">Express Interest</h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white/70">Estimated Rate</Label>
                <Input
                  value={interestForm.estimated_rate}
                  onChange={(e) => setInterestForm({ ...interestForm, estimated_rate: e.target.value })}
                  placeholder="e.g., 10-12%"
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                />
              </div>
              
              <div>
                <Label className="text-white/70">Points</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={interestForm.estimated_points}
                  onChange={(e) => setInterestForm({ ...interestForm, estimated_points: e.target.value })}
                  placeholder="e.g., 2"
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                />
              </div>
              
              <div>
                <Label className="text-white/70">Proposed Terms</Label>
                <Input
                  value={interestForm.proposed_terms}
                  onChange={(e) => setInterestForm({ ...interestForm, proposed_terms: e.target.value })}
                  placeholder="e.g., 12 month term, interest only"
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                />
              </div>
              
              <div>
                <Label className="text-white/70">Notes</Label>
                <textarea
                  value={interestForm.notes}
                  onChange={(e) => setInterestForm({ ...interestForm, notes: e.target.value })}
                  placeholder="Additional information..."
                  className="w-full bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] text-white rounded-xl p-2 h-20 resize-none"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="ghost"
                onClick={() => setShowInterestModal(false)}
                className="text-white/70"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleExpressInterest(selectedRequest?.request_id)}
                className="bg-gold hover:bg-gold"
              >
                Submit Interest
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AI Assistant - Floating */}
      <AIAssistant userRole="lender" variant="floating" />
    </div>
  );
}
